import React, { useState, useEffect, useCallback } from 'react';
import { 
  FolderKanban, 
  ClipboardList, 
  ChevronRight, 
  Layers, 
  CalendarCheck2, 
  ArrowLeft,
  Briefcase
} from 'lucide-react';
import { ProjectItem } from '../types.ts';
import { getInitialProjectsForCostCenter } from '../data/initialProjectsData.ts';
import { dataService } from '../lib/dataService.ts';
import { ProjectsListSubpage } from './ProjectsListSubpage.tsx';
import { WorkPlanGanttSubpage } from './WorkPlanGanttSubpage.tsx';

interface ProjectsManagerProps {
  activeCostCenter?: {
    id: string;
    name: string;
    code?: string;
  } | null;
  initialTab?: 'list' | 'workplan';
}

export const ProjectsManager: React.FC<ProjectsManagerProps> = ({
  activeCostCenter,
  initialTab = 'list'
}) => {
  const businessUnitName = activeCostCenter?.name || 'Bajío';
  const costCenterId = activeCostCenter?.id || 'CC-MAZDA-BAJIO';

  const storageKey = `snf_projects_${costCenterId}`;
  const deletedKey = `snf_deleted_projects_${costCenterId}`;
  const globalDeletedKey = `snf_deleted_projects_all`;
  const initializedKey = `snf_projects_initialized_${costCenterId}`;

  // Safe helper to read deleted IDs
  const getDeletedIds = useCallback((): string[] => {
    try {
      const ccDel = JSON.parse(localStorage.getItem(deletedKey) || '[]');
      const globalDel = JSON.parse(localStorage.getItem(globalDeletedKey) || '[]');
      return Array.from(new Set([...ccDel, ...globalDel]));
    } catch {
      return [];
    }
  }, [deletedKey, globalDeletedKey]);

  // Initial State: Respects deleted tombstones and empty state
  const [projects, setProjects] = useState<ProjectItem[]>(() => {
    try {
      const deletedIds = getDeletedIds();
      const isInitialized = localStorage.getItem(initializedKey) === 'true' || localStorage.getItem('snfsic_initialized_projects') === 'true';
      const saved = localStorage.getItem(storageKey);

      if (saved !== null) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter(p => !deletedIds.includes(p.id));
        }
      }

      if (isInitialized) {
        return [];
      }
    } catch (e) {
      console.error('Error loading projects from localStorage', e);
    }

    const initial = getInitialProjectsForCostCenter(costCenterId, businessUnitName).filter(p => !getDeletedIds().includes(p.id));
    try {
      localStorage.setItem(storageKey, JSON.stringify(initial));
      localStorage.setItem(initializedKey, 'true');
    } catch {}
    return initial;
  });

  const [activeTab, setActiveTab] = useState<'list' | 'workplan'>(initialTab);
  const [selectedProjectIdForGantt, setSelectedProjectIdForGantt] = useState<string | undefined>(undefined);

  // Sync to localStorage and dataService on load & subscribe to real-time changes
  useEffect(() => {
    let isMounted = true;

    // Load initial data through dataService
    dataService.getProjects(costCenterId, businessUnitName).then((items) => {
      if (isMounted) {
        const deletedIds = getDeletedIds();
        setProjects(items.filter(p => !deletedIds.includes(p.id)));
      }
    }).catch(err => {
      console.warn('Error loading projects from dataService:', err);
    });

    // Real-time synchronization subscription
    const unsubscribe = dataService.subscribeToProjects(costCenterId, (updatedItems) => {
      if (isMounted) {
        const deletedIds = getDeletedIds();
        setProjects(updatedItems.filter(p => !deletedIds.includes(p.id)));
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [costCenterId, businessUnitName, getDeletedIds]);

  // Update or create a single project
  const handleSaveProject = async (updatedProject: ProjectItem) => {
    const projectWithCC: ProjectItem = {
      ...updatedProject,
      costCenterId: updatedProject.costCenterId || costCenterId,
      costCenterName: updatedProject.costCenterName || businessUnitName
    };

    setProjects(prev => {
      const index = prev.findIndex(p => p.id === projectWithCC.id);
      let newArr: ProjectItem[];
      if (index >= 0) {
        newArr = [...prev];
        newArr[index] = projectWithCC;
      } else {
        newArr = [projectWithCC, ...prev];
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(newArr));
        localStorage.setItem(initializedKey, 'true');
        localStorage.setItem('snfsic_initialized_projects', 'true');
      } catch (e) {
        console.error('Error saving projects to localStorage', e);
      }
      return newArr;
    });

    try {
      await dataService.saveProject(projectWithCC);
    } catch (e) {
      console.error('Error saving project to dataService', e);
    }
  };

  // Permanently delete project with data integrity & no resurrection
  const handleDeleteProject = async (projectId: string) => {
    // 1. Immediately update local state
    setProjects(prev => {
      const filtered = prev.filter(p => p.id !== projectId);
      try {
        localStorage.setItem(storageKey, JSON.stringify(filtered));
        localStorage.setItem(initializedKey, 'true');
        localStorage.setItem('snfsic_initialized_projects', 'true');
      } catch (e) {
        console.error('Error saving projects after delete', e);
      }
      return filtered;
    });

    // 2. Track deleted ID in tombstones
    try {
      const ccDel: string[] = JSON.parse(localStorage.getItem(deletedKey) || '[]');
      if (!ccDel.includes(projectId)) {
        ccDel.push(projectId);
        localStorage.setItem(deletedKey, JSON.stringify(ccDel));
      }
      const globalDel: string[] = JSON.parse(localStorage.getItem(globalDeletedKey) || '[]');
      if (!globalDel.includes(projectId)) {
        globalDel.push(projectId);
        localStorage.setItem(globalDeletedKey, JSON.stringify(globalDel));
      }
    } catch (e) {
      console.error('Error updating deleted projects list', e);
    }

    // 3. Clear selected project for Gantt if it was the deleted one
    if (selectedProjectIdForGantt === projectId) {
      setSelectedProjectIdForGantt(undefined);
    }

    // 4. Delete permanently in dataService and Firestore
    try {
      await dataService.deleteProject(projectId, costCenterId);
    } catch (e) {
      console.error('Error deleting project in dataService', e);
    }
  };

  // Jump to Gantt for a specific project
  const handleSelectProjectForGantt = (projectId: string) => {
    setSelectedProjectIdForGantt(projectId);
    setActiveTab('workplan');
  };

  return (
    <div className="w-full space-y-6 pb-12">
      
      {/* TOP HEADER SECTION MATCHING USER SCREENSHOT */}
      <div className="space-y-4">
        
        {/* Main Title Banner */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-950/40 border border-red-500/30 flex items-center justify-center text-red-500 shadow-lg shadow-red-950/40">
            <FolderKanban className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-black text-white tracking-tight">Proyectos</h2>
            <p className="text-xs text-slate-400">
              Gestión de proyectos para la unidad de negocio {businessUnitName}.
            </p>
          </div>
        </div>

        {/* 2 NAVIGATION CARDS MATCHING USER SCREENSHOT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          
          {/* Card 1: Lista de Proyectos */}
          <button
            onClick={() => setActiveTab('list')}
            className={`w-full text-left p-4 rounded-2xl border transition-all cursor-pointer flex items-center justify-between group ${
              activeTab === 'list'
                ? 'bg-[#1a1b24] border-red-500/80 ring-1 ring-red-500/50 shadow-lg shadow-red-950/20'
                : 'bg-[#15161c] hover:bg-[#191a22] border-[#272935] hover:border-[#3a3d4f]'
            }`}
          >
            <div className="flex items-center gap-3.5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                activeTab === 'list'
                  ? 'bg-red-600/20 text-red-500 border border-red-500/40'
                  : 'bg-red-950/30 text-red-400/80 group-hover:text-red-400'
              }`}>
                <FolderKanban className="w-5 h-5" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-sm font-bold text-white group-hover:text-red-400 transition-colors">
                  Lista de Proyectos
                </h4>
                <p className="text-xs text-slate-400">
                  Gestión de lista de proyectos.
                </p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 transition-transform ${
              activeTab === 'list' ? 'text-red-400 translate-x-0.5' : 'text-slate-500 group-hover:text-slate-300'
            }`} />
          </button>

          {/* Card 2: Plan de trabajo */}
          <button
            onClick={() => setActiveTab('workplan')}
            className={`w-full text-left p-4 rounded-2xl border transition-all cursor-pointer flex items-center justify-between group ${
              activeTab === 'workplan'
                ? 'bg-[#1a1b24] border-red-500/80 ring-1 ring-red-500/50 shadow-lg shadow-red-950/20'
                : 'bg-[#15161c] hover:bg-[#191a22] border-[#272935] hover:border-[#3a3d4f]'
            }`}
          >
            <div className="flex items-center gap-3.5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                activeTab === 'workplan'
                  ? 'bg-red-600/20 text-red-500 border border-red-500/40'
                  : 'bg-red-950/30 text-red-400/80 group-hover:text-red-400'
              }`}>
                <ClipboardList className="w-5 h-5" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-sm font-bold text-white group-hover:text-red-400 transition-colors">
                  Plan de trabajo
                </h4>
                <p className="text-xs text-slate-400">
                  Gestión de plan de trabajo.
                </p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 transition-transform ${
              activeTab === 'workplan' ? 'text-red-400 translate-x-0.5' : 'text-slate-500 group-hover:text-slate-300'
            }`} />
          </button>

        </div>

      </div>

      {/* RENDER ACTIVE SUBPAGE */}
      <div className="pt-2">
        {activeTab === 'list' ? (
          <ProjectsListSubpage
            projects={projects}
            businessUnitName={businessUnitName}
            onSelectProjectForGantt={handleSelectProjectForGantt}
            onSaveProject={handleSaveProject}
            onDeleteProject={handleDeleteProject}
          />
        ) : (
          <WorkPlanGanttSubpage
            projects={projects}
            businessUnitName={businessUnitName}
            selectedProjectId={selectedProjectIdForGantt}
            onUpdateProject={handleSaveProject}
            onSaveProject={handleSaveProject}
            onDeleteProject={handleDeleteProject}
          />
        )}
      </div>

    </div>
  );
};
